import { z } from 'zod';

import { EmployeeGender, EmployeeIdType, EmployeeMaritalStatus } from '@/common/graphql';
// Imported directly from its leaf module, not the `@/common/utils` barrel: that barrel also
// re-exports `api.utils.tsx`, which pulls in the entire `@/common/components` tree and, via
// `contexts/EmployeeDetailContext.tsx`, back into `@/common/types` - a circular import that left
// `EmployeeDetail` undefined at module-evaluation time in any test importing this file's chain.
import { CountryCode } from '../../utils/country-codes';
import { EmployeeDependent, EmployeeEmergencyContact, EmployeeVisa } from '../core';

export const EmployeePersonalInformation = z.object({
  id: z.string().default(''),
  gender: z.nativeEnum(EmployeeGender).nullable().default(null),
  dateOfBirth: z.string().nullable().default(null),
  age: z.number().nullable().default(null),
  personalPhoneNumber: z.string().nullable().default(''),
  personalPhoneNumberCountryCode: z.nativeEnum(CountryCode).nullable().default(null),
  personalPhoneNumberCountryIsdCode: z.string().nullable().default(null),
  personalEmail: z.string().nullable().default(null),
  currentAddress: z.string().nullable().default(null),
  permanentAddress: z.string().nullable().default(null),
  maritalStatus: z.nativeEnum(EmployeeMaritalStatus).nullable().default(null),
  emergencyContacts: z.array(EmployeeEmergencyContact).default([]).catch([]),
  visas: z.array(EmployeeVisa).default([]).catch([]),
  dependents: z.array(EmployeeDependent).default([]).catch([]),
  residentialCountryId: z.string().nullable().default(''),
  // ID
  idType: z.nativeEnum(EmployeeIdType).nullable().default(null),
  idNumber: z.string().nullable().default(null),
  idIssuedDate: z.string().nullable().default(null),
  idExpiryDate: z.string().nullable().default(null),
  idIssuedPlace: z.string().nullable().default(null),
  // Passport
  passportNumber: z.string().nullable().default(null),
  passportName: z.string().nullable().default(null),
  passportIssuedPlace: z.string().nullable().default(null),
  passportIssuedDate: z.string().nullable().default(null),
  passportExpiryDate: z.string().nullable().default(null),
  placeOfBirth: z.string().nullable().default(null),
  // Social Insurance
  taxCode: z.string().nullable().default(null),
  socialInsuranceNumber: z.string().nullable().default(null),
  pensionVoluntaryContributionRate: z.string().nullable().default(null),
  taxWithholdingRate: z.string().nullable().default(null),
  nhiDependents: z.number().nullable().default(null),
  socialInsuranceNotes: z.string().nullable().default(null),
  // Bank
  bankCountryCode: z.string().nullable().default(null),
  bankName: z.string().nullable().default(null),
  branchName: z.string().nullable().default(null),
  accountNumber: z.string().nullable().default(null),
  accountHolderName: z.string().nullable().default(null),
  // Consulting Fee
  consultingFeeBankCurrencyId: z.string().nullable().default(null),
  consultingFeeBankCountryCode: z.string().nullable().default(null),
  consultingFeeBankName: z.string().nullable().default(null),
  consultingFeeBranchName: z.string().nullable().default(null),
  consultingFeeAccountNumber: z.string().nullable().default(null),
  consultingFeeAccountHolderName: z.string().nullable().default(null),
  remarks: z.string().nullable().default(null),
});
export type EmployeePersonalInformationType = z.infer<typeof EmployeePersonalInformation>;

export const EmployeeMaritalStatusLabels: Record<EmployeeMaritalStatus, string> = {
  [EmployeeMaritalStatus.Single]: 'Single',
  [EmployeeMaritalStatus.Married]: 'Married',
};

export const EmployeeIdTypeLabels: Record<EmployeeIdType, string> = {
  [EmployeeIdType.DriverLicense]: 'Driver License',
  [EmployeeIdType.NationalId]: 'National ID',
};
